/**
 * Created by Ning Jiang on 2022-06-10.
 */

import {BitmapTextStyleOptions} from "@nolimitcity/slot-game/bin/text/BitmapTextStyleOptions";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {gsap} from "gsap";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {DeadwoodRipGameAssets} from "../DeadwoodRipGameAssets";
import {DeadwoodRip} from "../DeadwoodRip";
import {DeadwoodRipLayers, Layers} from "./DeadwoodRipLayers";

export class Ways extends PIXI.Container {

    private readonly _numberStyle:BitmapTextStyleOptions = {
        fontName : DeadwoodRipGameAssets.BITMAP_FONT_WAYS,
        fontSize : 12,
        tint : 0xffffff
    };

    private _waysContainer:PIXI.Container;
    private _waysNumber:PIXI.BitmapText;
    private _waysName:PIXI.Sprite;
    // private _smoke:PIXI.Sprite;

    private _currentScale:number;
    private _currentSymbolValues:number[][];

    public get currentValue():number {
        if(!this._currentSymbolValues) {
            return 1;
        }
        return this._currentSymbolValues.reduce((a, b) => {
            return a * ArrayHelper.arraySum(b);
        }, 1);
    }

    constructor() {
        super();

        this._waysContainer = new PIXI.Container();
        this.addChild(this._waysContainer);

        // this._smoke = new PIXI.Sprite(TrueKult.assetLoader.getTextures("smoke/waysSmoky")[0]);
        // this._smoke.anchor.set(0.5, 0.5);
        // this._smoke.alpha = 0;
        // this._waysContainer.addChild(this._smoke);

        this._waysNumber = new PIXI.BitmapText("0123456789", this._numberStyle);
        (<PIXI.Point>this._waysNumber.anchor).set(0.5, 1.3);
        this._waysNumber.y = -36;
        this._waysNumber.letterSpacing = -20;
        this._waysContainer.addChild(this._waysNumber);

        this._waysName = new PIXI.Sprite(DeadwoodRip.assetLoader.getTextures(`totalways/ways`)[0]);
        this._waysName.anchor.set(0.5, 0.5);
        // this._waysName.scale.set(0.66);
        this._waysContainer.addChild(this._waysName);

        DeadwoodRipLayers.getLayer(Layers.FOREGROUND_TOP)?.addChild(this);
        this.position.set(68, 118);

        this.resetReel(false);
    }

    public getSymbolValue(reelId:number, symId:number):number {
        return this._currentSymbolValues[reelId][symId];
    }

    public resetReel(playAni:boolean = true):void {
        const oldValue = this.currentValue;
        this._currentSymbolValues = [
            [1, 1, 1],
            [1, 1, 1, 1],
            [1, 1, 1, 1],
            [1, 1, 1, 1],
            [1, 1]
        ];

        this.updateAllWays(playAni && (oldValue != this.currentValue), true);
    }

    private updateSize():void {
        const landscapeMaxWidth = 120;

        this._waysNumber.scale.set(1);
        this._waysNumber.updateTransform();
        if(this._waysNumber.textWidth > landscapeMaxWidth) {
            this._waysNumber.scale.set(MathHelper.roundToDecimals(landscapeMaxWidth / this._waysNumber.textWidth, 2));
        }
        this._currentScale = this._waysNumber.scale.x;
    }

    public updateAllWays(playAni:boolean = true, reset:boolean = false):void {
        if(DeadwoodRip.model.isRestore) {
            playAni = false;
        }
        this._waysNumber.text = this.currentValue.toString();
        this.updateSize();
        if(!playAni) {
            return;
        }
        const tl = gsap.timeline();
        tl.add([
            gsap.to(this._waysNumber.scale, {
                duration : 0.2,
                x : this._currentScale * 1.3,
                y : this._currentScale * 1.3
            }),
            gsap.to(this._waysNumber, {duration : 0.2, pixi : {brightness : 2}}),
            gsap.to(this._waysName, {duration : 0.2, pixi : {brightness : 2}}),
            // gsap.fromTo(this._smoke, {alpha : 0}, {duration : 0.2, alpha : reset ? 0.5 : 1}),
            // gsap.fromTo(this._smoke.scale, {x : 0.54, y : 0.54}, {duration : 0.2, x : 0.93, y : 0.93}),
            // () => {TrueKultSound.playWaysUpdate();}
        ]);
        tl.add([
            gsap.to(this._waysNumber.scale, {
                duration : 0.5,
                x : this._currentScale,
                y : this._currentScale
            }),
            gsap.to(this._waysNumber, {duration : 0.5, pixi : {brightness : 1}}),
            gsap.to(this._waysName, {duration : 0.5, pixi : {brightness : 1}}),
            // gsap.to(this._smoke, {duration : 0.167, alpha : 0}),
            // gsap.to(this._smoke.scale, {duration : 0.167, x : 1, y : 1})
        ]);
    }

    public updateSymbolWays(reelId:number, symId:number, ways:number, updateDisplay:boolean = true):void {
        const oldValue = this._currentSymbolValues[reelId][symId];
        if(oldValue != ways) {
            this._currentSymbolValues[reelId][symId] = ways;
            if(updateDisplay) {
                this.updateAllWays(true);
            }
        }
    }

    public reelSplit(reelId:number):void {
        // this.playSplit("WRSSplit");
        this._currentSymbolValues[reelId].forEach((symbolSize, symId) => {
            this.updateSymbolWays(reelId, symId, this._currentSymbolValues[reelId][symId] * 2, false);
        });
        this.updateAllWays(true);
    }

    public xSplit():void {
        // this.playSplit("WXSSplit");
        this._currentSymbolValues[0][1] *= 2;
        this.doXSplitOnSymbol(1, 1);
        this.doXSplitOnSymbol(1, 2);
        this.doXSplitOnSymbol(2, 1);
        this.doXSplitOnSymbol(2, 2);
        this.doXSplitOnSymbol(3, 1);
        this.doXSplitOnSymbol(3, 2);
        this._currentSymbolValues[4][0] = 1;
        this._currentSymbolValues[4][1] = 1;
        this.updateAllWays(true);
    }

    private doXSplitOnSymbol(reelId:number, symbolId:number):void {
        const symName = DeadwoodRip.reels[reelId].getSymbol(symbolId).symbolName;
        if(symName.includes("S1") || symName.includes("S3") || symName.includes("WXN")) {
            this._currentSymbolValues[reelId][symbolId] = 1;
            return;
        }
        this._currentSymbolValues[reelId][symbolId] *= 2;
    }

    public setMode(mode:string):void {
        if(mode.includes("SUPER")) {
            this._waysNumber.tint = 0x251609;
            this._waysName.tint = 0x251609;
        } else if(mode.includes("FREESPIN")) {
            this._waysNumber.tint = 0x251609;
            this._waysName.tint = 0x251609;
        } else {
            this._waysNumber.tint = 0x373631;
            this._waysName.tint = 0x373631;
        }
    }
}